import React, { useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import {
  mdiAlarm,
  mdiArrowDownThin,
  mdiBellMinus,
  mdiClockAlertOutline,
  mdiCurrentAc,
  mdiFlash,
  mdiFlashAlertOutline,
  mdiPowerPlugOffOutline,
  mdiScaleUnbalanced,
  mdiSwapHorizontal,
  mdiThermometer,
} from "@mdi/js";
import MdiIcon from "../../../components/device/app/MdiIcon";
import { DEVICE_TYPES, MODELS, TRAITS, isModel, isType } from "../../../constants/devices";
import { SettingsPage, SettingsSkeleton, Unavailable } from "./shared";
import { NumberDialog, SwitchRow, ValueRow, isEnabled, useMeterSettings, valueOf } from "./meter";

const ALARMS = Number(TRAITS.METER_ALARMS);

// The app's alarm sections with their trait 1403 ids and ranges. The meter
// reports each as <key>_threshold, <key>_alarm_enable, <key>_recovery_delay
// and <key>_event_duration.
const SECTIONS = [
  {
    key: "high_temperature",
    icon: mdiThermometer,
    title: "High Temperature alarm",
    threshold: { id: 18, unit: "°C", min: 10, max: 85, description: "Set the maximum safe temperature limit." },
    alarm: { id: 24, description: "Enable alerts when the temperature exceeds the limit." },
  },
  {
    key: "leakage",
    icon: mdiFlashAlertOutline,
    title: "Leakage current",
    threshold: { id: 19, unit: "mA", min: 10, max: 99, description: "Set the maximum safe leakage current limit." },
    alarm: { id: 25, description: "Enable alerts when leakage current exceeds the limit." },
    recovery: {
      id: 1,
      unit: "s",
      min: 1,
      max: 9999,
      title: "Leakage current recovery power-on delay time",
      description: "Set the delay before power is restored after leakage recovers.",
    },
  },
  {
    key: "over_current",
    icon: mdiFlash,
    title: "Over current",
    threshold: { id: 16, unit: "A", min: 1, max: 1000, description: "Set the maximum safe current limit." },
    alarm: { id: 22, description: "Enable alerts when current exceeds the safe limit." },
    recovery: {
      id: 3,
      unit: "s",
      min: 1,
      max: 9999,
      title: "Overcurrent recovery power-on delay time",
      description: "Set the delay before power is restored after current returns to normal.",
    },
    event: {
      id: 7,
      unit: "s",
      min: 1,
      max: 999,
      title: "Overcurrent Event Threshold Time",
      description: "Set how long abnormal current must persist before triggering an event.",
    },
  },
  {
    key: "over_voltage",
    icon: mdiAlarm,
    title: "Over voltage alarm",
    threshold: { id: 14, unit: "V", min: 120, max: 300, description: "Set the maximum safe voltage limit." },
    alarm: { id: 20, description: "Enable alerts when voltage exceeds the safe limit." },
  },
  {
    key: "under_voltage",
    icon: mdiArrowDownThin,
    title: "Under voltage",
    threshold: { id: 15, unit: "V", min: 80, max: 210, description: "Set the minimum safe voltage limit." },
    alarm: { id: 21, description: "Enable alerts when voltage falls below the safe limit." },
  },
  {
    key: "voltage_imbalance",
    icon: mdiScaleUnbalanced,
    title: "Voltage three-phase imbalance alarm",
    threePhaseOnly: true,
    threshold: { id: 27, unit: "%", min: 0, max: 100, description: "Set the maximum allowed voltage difference between phases." },
    alarm: { id: 31, description: "Enable alerts when the phase voltages become unbalanced." },
  },
  {
    key: "current_imbalance",
    icon: mdiCurrentAc,
    title: "Current three-phase imbalance alarm",
    threePhaseOnly: true,
    threshold: { id: 26, unit: "%", min: 0, max: 100, description: "Set the maximum allowed current difference between phases." },
    alarm: { id: 28, description: "Enable alerts when the phase currents become unbalanced." },
  },
  {
    key: "phase_loss",
    icon: mdiPowerPlugOffOutline,
    title: "Three phase lack of phase alarm",
    threePhaseOnly: true,
    alarm: { id: 29, description: "Enable alerts when any of the three phases is lost." },
  },
  {
    key: "phase_sequence",
    icon: mdiSwapHorizontal,
    title: "Reverse phase sequence alarm",
    threePhaseOnly: true,
    alarm: { id: 30, description: "Enable alerts when the phase order is reversed." },
  },
  {
    // The app's "Voltage event time threshold" (id 6) isn't here: its
    // settings screen sends 1403 id 6 for the current multiplier (see
    // EnergyMeterSettings.js).
    key: "voltage",
    icon: mdiClockAlertOutline,
    title: "Voltage Delays",
    recovery: {
      id: 2,
      unit: "s",
      min: 1,
      max: 9999,
      title: "Voltage event recovery power-on delay time",
      description: "Set the delay before power is restored after voltage returns to normal.",
    },
  },
  {
    key: "lost_flow",
    icon: mdiBellMinus,
    title: "Lost flow alarm",
    threshold: { id: 17, unit: "A", min: 1, max: 1000, description: "Set the minimum current level to detect lost flow." },
    alarm: { id: 23, description: "Enable alerts when current flow is lost." },
    recovery: {
      id: 4,
      unit: "s",
      min: 1,
      max: 9999,
      title: "Lost current recovery delay time",
      description: "Set the delay before power is restored after current flow recovers.",
    },
    event: {
      id: 8,
      unit: "s",
      min: 1,
      max: 999,
      title: "Time Threshold of Lost Flow Event",
      description: "Set how long lost current must persist before triggering an event.",
    },
  },
];

// The section's name, read before a row's own label, since every section has
// a "Threshold Settings" and a "Switch Alarm".
const Named = ({ section, children }) => (
  <>
    <span className="hx-set-sr">{section.title}: </span>
    {children}
  </>
);

// Alarm Setting for the energy meters (app/screens/EnergyMeterAlarmSetting.js):
// per alarm, its limit, its on/off switch and its delays.
function EnergyMeterAlarms() {
  const { state } = useLocation();
  const device = state?.data;
  const supported = isType(device, DEVICE_TYPES.ENERGY_METER) && Boolean(device?.deviceId);
  const { settings, status, busy, send, retry } = useMeterSettings(supported ? device : null);
  const [editor, setEditor] = useState(null);

  if (!device) return <Redirect to="/dashboard" />;

  // key is where the meter reports the value; title is the editor's heading.
  const edit = (row, key, title) => {
    if (!busy) setEditor({ ...row, key, title, value: valueOf(settings, key) });
  };

  const saveNumber = (value) => {
    const row = editor;
    setEditor(null);
    send({ id: row.id, trait: ALARMS, value }, row.key);
  };

  const toggle = (section) => {
    if (busy) return;
    const key = `${section.key}_alarm_enable`;
    send({ id: section.alarm.id, trait: ALARMS, value: Number(!isEnabled(settings, key)) }, key);
  };

  const shown = (row, key) => `${valueOf(settings, key)}${row.unit}`;

  const renderSection = (section) => {
    const { key, threshold, alarm, recovery, event } = section;
    const titleId = `hx-set-em-${key}`;
    return (
      <section key={key} className="hx-set-em-section" aria-labelledby={titleId}>
        <h2 id={titleId} className="hx-set-em-section-title">
          <MdiIcon path={section.icon} size={20} />
          {section.title}
        </h2>
        <ul className="hx-set-em-list">
          {threshold ? (
            <ValueRow
              title={<Named section={section}>Threshold Settings</Named>}
              description={threshold.description}
              value={shown(threshold, `${key}_threshold`)}
              onOpen={() => edit(threshold, `${key}_threshold`, `${section.title} - Threshold Settings`)}
            />
          ) : null}
          {alarm ? (
            <SwitchRow
              id={`hx-set-em-${key}-alarm`}
              title={<Named section={section}>Switch Alarm</Named>}
              description={alarm.description}
              checked={isEnabled(settings, `${key}_alarm_enable`)}
              onToggle={() => toggle(section)}
            />
          ) : null}
          {recovery ? (
            <ValueRow
              title={recovery.title}
              description={recovery.description}
              value={shown(recovery, `${key}_recovery_delay`)}
              onOpen={() => edit(recovery, `${key}_recovery_delay`, recovery.title)}
            />
          ) : null}
          {event ? (
            <ValueRow
              title={event.title}
              description={event.description}
              value={shown(event, `${key}_event_duration`)}
              onOpen={() => edit(event, `${key}_event_duration`, event.title)}
            />
          ) : null}
        </ul>
      </section>
    );
  };

  let body;
  if (!supported) {
    body = <Unavailable />;
  } else if (status === "loading") {
    body = <SettingsSkeleton tall />;
  } else if (status === "error") {
    body = (
      <div className="hx-empty hx-set-empty" role="alert">
        Couldn't read this device's settings.
        <button type="button" className="hx-btn" onClick={retry}>
          Try again
        </button>
      </div>
    );
  } else {
    // The imbalance, lack of phase and phase sequence alarms are three-phase only.
    const threePhase = isModel(device, MODELS.W3EMP_1);
    body = SECTIONS.filter((section) => threePhase || !section.threePhaseOnly).map(renderSection);
  }

  return (
    <SettingsPage title="Alarm Setting" device={device} busy={busy}>
      {body}
      {editor ? <NumberDialog editor={editor} onClose={() => setEditor(null)} onSave={saveNumber} /> : null}
    </SettingsPage>
  );
}

export default EnergyMeterAlarms;
